import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { actions as projectActions } from 'spw-redux/modules/projects/actions';
import { Project as projectApi } from 'spw-api';
import { handleError } from 'spw-redux/modules/UI/messages/actions';
import {
  currentProject as currentProjectSelector,
  currentProject,
} from 'spw-redux/modules/projects/selectors';
import {
  IProject,
  IProjectSpaceTypeCategory,
  IProjectSpaceTypeSubcategory,
} from 'spw-redux/modules/projects/types';
import { omit, snakeCase } from 'lodash';
import { removeTypeNameFromObject } from 'spw-shared/utils';
import { getDefaultSpaceTypeCategory } from 'spw-redux/modules/project-space-type-categories/utils';
import { APIResponse } from 'spw-redux/types';
import { CanDeleteEnum } from 'spw-shared/types';
import { fetchProjectSpaceTypes } from 'spw-redux/modules/space_types/actions';
import { fetchAllChildWorksheetSpaces } from 'spw-redux/modules/calculators/actions';
import { EXISTING_SITE_ANALYSIS, PROGRAMMING } from 'spw-constants';
import {
  actions,
  DELETE_CUSTOM_SPACE_TYPE_SUBCATEGORIES,
  GET_PROJECT_SPACE_TYPE_CATEGORY_USAGE,
  setAssociatedSpaceTypesAndFloorplans,
  setAssociatedSpaceTypesAndWorksheets,
  setCanDeleteSubcategory,
  UPDATE_PROJECT_SPACE_TYPE_CATEGORY,
} from './actions';

function* updateProjectSpaceTypeCategory(
  action: ReturnType<typeof actions.updateProjectSpaceTypeCategory>
) {
  try {
    const { spaceTypeCategory, callback } = action.payload;

    if (!spaceTypeCategory) {
      throw new Error(
        'There was an error trying to update the space type category'
      );
    }

    const newProjectSpaceTypeCategory = omit(
      removeTypeNameFromObject({
        ...spaceTypeCategory,
        subcategories: spaceTypeCategory.subcategories?.map(
          (subCategory: IProjectSpaceTypeSubcategory) => {
            return {
              ...omit(removeTypeNameFromObject(subCategory), ['parentId']),
              defaultSpaceTypeCategory: {
                category: snakeCase(
                  subCategory.defaultSpaceTypeCategory.category
                ),
              },
            };
          }
        ),
      }),
      ['defaultSpaceTypeCategory', 'parentId']
    );

    const {
      data: { projectSpaceTypeCategory },
    } = yield call(
      projectApi.updateProjectSpaceTypeCategory,
      newProjectSpaceTypeCategory
    );

    const project: ReturnType<typeof currentProject> = yield select(
      currentProject
    );
    const { spaceTypeCategories } = project;

    let index;
    if (projectSpaceTypeCategory?.parentId) {
      index = spaceTypeCategories.findIndex(
        (currentSpaceTypeCategory: IProjectSpaceTypeCategory) =>
          currentSpaceTypeCategory.id === projectSpaceTypeCategory.parentId
      );
      const parentCategory = spaceTypeCategories[index];
      const subcatIndex = parentCategory?.subcategories.findIndex(
        (subcategory: IProjectSpaceTypeSubcategory) =>
          subcategory.id === projectSpaceTypeCategory.id
      );
      yield put(
        projectActions.setCurrentProject({
          ...project,
          spaceTypeCategories: [
            ...spaceTypeCategories.slice(0, index),
            {
              ...parentCategory,
              defaultSpaceTypeCategory: getDefaultSpaceTypeCategory(
                parentCategory.defaultSpaceTypeCategory
              ),
              subcategories: [
                ...parentCategory.subcategories.slice(0, subcatIndex),
                {
                  ...projectSpaceTypeCategory,
                  defaultSpaceTypeCategory: getDefaultSpaceTypeCategory(
                    projectSpaceTypeCategory.defaultSpaceTypeCategory
                  ),
                },
                ...parentCategory.subcategories.slice(subcatIndex + 1),
              ],
            },
            ...spaceTypeCategories.slice(index + 1),
          ],
        })
      );
    } else {
      index = spaceTypeCategories.findIndex(
        (currentSpaceTypeCategory: IProjectSpaceTypeCategory) =>
          currentSpaceTypeCategory.id === projectSpaceTypeCategory.id
      );
      yield put(
        projectActions.setCurrentProject({
          ...project,
          spaceTypeCategories: [
            ...spaceTypeCategories.slice(0, index),
            {
              ...projectSpaceTypeCategory,
              defaultSpaceTypeCategory: getDefaultSpaceTypeCategory(
                projectSpaceTypeCategory.defaultSpaceTypeCategory
              ),
              subcategories: projectSpaceTypeCategory.subcategories?.map(
                (subCategory: IProjectSpaceTypeSubcategory) => ({
                  ...subCategory,
                  defaultSpaceTypeCategory: getDefaultSpaceTypeCategory(
                    subCategory.defaultSpaceTypeCategory
                  ),
                })
              ),
            },
            ...spaceTypeCategories.slice(index + 1),
          ],
        })
      );
    }

    if (callback) callback();
  } catch (e) {
    yield put(handleError(e));
  }
}

function* deleteCustomSpaceTypeSubcategories(
  action: ReturnType<typeof actions.deleteCustomSpaceTypeSubcategories>
) {
  try {
    const { categoryId, subcategoryIds, callback } = action.payload;
    const project: ReturnType<typeof currentProject> = yield select(
      currentProject
    );
    const {
      data: { deleteCustomSpaceTypeCategories },
    } = yield call(
      projectApi.deleteCustomSpaceTypeCategories,
      project.id,
      subcategoryIds
    );

    if (deleteCustomSpaceTypeCategories.numberOfCategoriesDeleted > 0) {
      const { spaceTypeCategories } = project;
      const index = project.spaceTypeCategories?.findIndex(
        (category: IProjectSpaceTypeCategory) => category.id === categoryId
      );

      if (index > -1) {
        const projectSpaceTypeCategory = spaceTypeCategories[index];
        const subcategories = projectSpaceTypeCategory.subcategories
          .slice()
          .filter((subcategory: IProjectSpaceTypeSubcategory) => {
            return !subcategoryIds.includes(subcategory.id);
          });

        yield put(
          projectActions.setCurrentProject({
            ...project,
            spaceTypeCategories: [
              ...spaceTypeCategories.slice(0, index),
              { ...projectSpaceTypeCategory, subcategories },
              ...spaceTypeCategories.slice(index + 1),
            ],
          })
        );
      }

      yield put(fetchProjectSpaceTypes());
      yield put(fetchAllChildWorksheetSpaces());
    }

    if (callback) {
      callback();
    }
  } catch (e) {
    yield put(handleError(e));
  }
}

function* getProjectSpaceTypeCategoryUsage(
  action: ReturnType<typeof actions.getProjectSpaceTypeCategoryUsage>
) {
  try {
    const { id } = action.payload;
    yield put(setCanDeleteSubcategory(CanDeleteEnum.QUERYING));
    const project: IProject = yield select(currentProjectSelector);

    if (project.type === EXISTING_SITE_ANALYSIS) {
      const response: APIResponse<typeof projectApi.getESASpaceTypeCategoryUsage> = yield call(
        projectApi.getESASpaceTypeCategoryUsage,
        id
      );

      const {
        data: { usage },
      } = response;

      yield put(
        setAssociatedSpaceTypesAndFloorplans(
          usage.spaceTypeCountForPstc,
          usage.floorplanCountForPstc
        )
      );
    } else if (project.type === PROGRAMMING) {
      const response: APIResponse<typeof projectApi.getProjectSpaceTypeCategoryUsage> = yield call(
        projectApi.getProjectSpaceTypeCategoryUsage,
        id
      );
      const {
        data: { usage },
      } = response;

      yield put(
        setAssociatedSpaceTypesAndWorksheets(
          usage.space_type_count_for_pstc,
          usage.worksheet_count_for_pstc
        )
      );
    }

    yield put(setCanDeleteSubcategory(CanDeleteEnum.TRUE));
  } catch (e) {
    setCanDeleteSubcategory(CanDeleteEnum.FALSE);
    yield put(handleError(e));
  }
}

function* saga() {
  yield all([
    takeLatest(
      UPDATE_PROJECT_SPACE_TYPE_CATEGORY,
      updateProjectSpaceTypeCategory
    ),
    takeLatest(
      GET_PROJECT_SPACE_TYPE_CATEGORY_USAGE,
      getProjectSpaceTypeCategoryUsage
    ),
    takeLatest(
      DELETE_CUSTOM_SPACE_TYPE_SUBCATEGORIES,
      deleteCustomSpaceTypeSubcategories
    ),
  ]);
}

export default saga;
